import { jsx as _jsx } from "react/jsx-runtime";
import { MenuRoot } from '#pplx/frontend/aether/components/DropdownMenu/internal/MenuRoot.js';
import { MenuCheckboxItem } from '#pplx/frontend/aether/components/internal/menu/MenuCheckboxItem.js';
import { MenuDestructiveItem } from '#pplx/frontend/aether/components/internal/menu/MenuDestructiveItem.js';
import { MenuFooter } from '#pplx/frontend/aether/components/internal/menu/MenuFooter.js';
import { MenuGroup } from '#pplx/frontend/aether/components/internal/menu/MenuGroup.js';
import { MenuHeader } from '#pplx/frontend/aether/components/internal/menu/MenuHeader.js';
import { MenuInfoItem } from '#pplx/frontend/aether/components/internal/menu/MenuInfoItem.js';
import { MenuItem } from '#pplx/frontend/aether/components/internal/menu/MenuItem.js';
import { MenuLinkItem } from '#pplx/frontend/aether/components/internal/menu/MenuLink.js';
import { MenuRadioGroup } from '#pplx/frontend/aether/components/internal/menu/MenuRadioGroup.js';
import { MenuRadioItem } from '#pplx/frontend/aether/components/internal/menu/MenuRadioItem.js';
import { MenuSeparator } from '#pplx/frontend/aether/components/internal/menu/MenuSeparator.js';
import { MenuSubmenu } from '#pplx/frontend/aether/components/internal/menu/MenuSubmenu.js';
import { MenuSubmenuDrilldownItem } from '#pplx/frontend/aether/components/internal/menu/MenuSubmenuDrilldownItem.js';
import { MenuSubmenuItem } from '#pplx/frontend/aether/components/internal/menu/MenuSubmenuItem.js';
import { MenuSwitchItem } from '#pplx/frontend/aether/components/internal/menu/MenuSwitchItem.js';
function DropdownMenuRoot(props) {
    return _jsx(MenuRoot, { ...props });
}
/**
 * Feature-rich dropdown menu with support for items, checkbox items, radio groups, submenus,
 * separators, and more. On mobile it renders as a bottom sheet.
 *
 * Hover-revealed trigger containers can rely on ancestor `:has([data-state=open])` styling (e.g.
 * `group-has-[[data-state=open]]/row:flex`) to keep the trigger's layout box while the menu is
 * open. A hidden `data-state="open"` sentinel stays mounted next to the trigger until the content
 * unmounts. Set disableAnimation when an action moves the trigger while opening another surface,
 * so the exiting menu cannot reposition or steal focus from the destination.
 *
 * @category components
 * @keywords menu, components, feature-rich, dropdown, with, support, items, checkbox, radio,
 *   groups, submenus, separators, more, mobile, renders, bottom, sheet, button, icon, tooltip,
 *   selectable submenu trigger, model options, onTriggerSelect, openInstantly, hover,
 *   trailing accessory, chevron, header, fixed, search, scroll, disableAnimation, animation,
 *   focus handoff, closing flash, trigger moves
 * @whenToUse Use DropdownMenu to present a list of actions, options, or navigation links
 *   triggered by a button or icon. It is suitable for contextual actions, option selectors,
 *   settings toggles, and navigation overflow menus. The compound component pattern
 *   (DropdownMenu.Item, DropdownMenu.CheckboxItem, DropdownMenu.RadioGroup, DropdownMenu.Submenu,
 *   etc.) supports a wide range of menu content from simple action lists to complex multi-level
 *   menus with stateful selections. On mobile, it automatically adapts to a bottom sheet pattern.
 * @guidelineDo
 *   - Set closeOnSelect={false} on DropdownMenu.Item for repeatable actions such as zoom controls.
 *   - Always use DropdownMenu and its compound components (DropdownMenu.Item,
 *     DropdownMenu.CheckboxItem, DropdownMenu.RadioItem, DropdownMenu.SwitchItem,
 *     DropdownMenu.Submenu) as the default choice for dropdown menus — they provide built-in
 *     accessibility, keyboard navigation, and mobile adaptation. Prefer DropdownMenu over
 *     MenuItemPrimitive in all standard cases
 *   - Use Button as the triggerElement for a pre-styled, accessible menu trigger
 *   - Organize related items using DropdownMenu.Group and DropdownMenu.Separator for visual and
 *     semantic grouping
 *   - Use DropdownMenu.CheckboxItem, DropdownMenu.RadioGroup/DropdownMenu.RadioItem, and
 *     DropdownMenu.SwitchItem for stateful selections within the menu
 *   - Set the align and side props to position the menu appropriately relative to the trigger
 *     context
 *   - Use the controlled isOpen/onToggle pattern when you need to programmatically manage menu
 *     visibility
 *   - Use the checked and onTriggerSelect props on DropdownMenu.SubmenuItem for selectable
 *     submenu triggers (e.g. model-option menus) — the trigger row exposes radio-item checked
 *     semantics and can select a value when activated while still opening its submenu.
 *     onTriggerSelect is ignored on mobile, where activating the row toggles the expandable
 *     section instead
 *   - Use trailingValue on DropdownMenu.SubmenuItem for the submenu's current selection or state.
 *     It renders before the submenu affordance on both desktop and mobile
 *   - Pass trailingAccessory to DropdownMenu.SubmenuItem to replace the default desktop submenu
 *     chevron, or null to remove it when the submenu panel should not read like a nested menu —
 *     mobile always keeps its chevron
 *   - Use trailingAccessory on DropdownMenu.RadioItem for a secondary interactive control on the
 *     row (e.g. a "more options" button) — the control should stop event propagation if
 *     activating it should not also select the row
 *   - Set openInstantly on DropdownMenu.Submenu for hover-driven submenus that should open
 *     immediately on mouse hover and skip the desktop open/close animation — it does not apply
 *     to mobile bottom sheets
 *   - Set disableAnimation when selecting a menu action moves the trigger while opening another
 *     surface (e.g. a side panel or inline form), so the exiting desktop menu cannot flash at a
 *     new position or steal focus from the destination — it does not apply to mobile bottom
 *     sheets, and other menus should keep their open/close animation
 *   - Use DropdownMenu.Header for controls such as search that must remain visible while menu
 *     items scroll
 *   - Set textOverflow="ellipsis" on DropdownMenu.Item or DropdownMenu.LinkItem when constrained
 *     menus should keep the primary label and subtitle to one line each
 * @guidelineDont
 *   - Don't reach for MenuItemPrimitive when DropdownMenu.Item can do the job —
 *     DropdownMenu.Item supports leadingAccessory, trailingAccessory, subtitle, and custom
 *     content. Only use MenuItemPrimitive as a last resort for truly custom item layouts
 *   - Don't use the deprecated __dangerouslySetZIndex prop — rely on the default portal stacking
 *     context instead
 *   - Don't nest DropdownMenu components deeply — use DropdownMenu.Submenu and
 *     DropdownMenu.SubmenuItem for hierarchical menus
 *   - Don't set modal to true unless the menu must block all background interaction, as it
 *     changes focus trapping behavior
 *   - Don't render heavy content inside menu items — menus should contain concise labels and
 *     lightweight icons
 *   - Don't use portalTargetElement unless you have a specific scrolling container constraint
 *     that requires it
 * @accessibility
 *   - Provides full WAI-ARIA menu pattern support including role="menu" and role="menuitem"
 *   - Keyboard navigation is handled automatically — Arrow keys move between items, Enter/Space
 *     activates, and Escape closes the menu
 *   - The Trigger renders with aria-haspopup and aria-expanded attributes, communicating menu
 *     state to assistive technology
 *   - On mobile viewports the menu renders as a MobileBottomSheet dialog instead of a dropdown,
 *     adapting the interaction pattern for touch users
 *   - DropdownMenu.CheckboxItem and DropdownMenu.RadioItem use appropriate ARIA roles
 *     (menuitemcheckbox, menuitemradio) for stateful selections
 *   - DestructiveItem confirmation dialogs announce bodyText, including rich text, as their
 *     accessible description
 * @relatedComponents button, icon, tooltip
 */
export const DropdownMenu = Object.assign(DropdownMenuRoot, {
    Item: MenuItem,
    DestructiveItem: MenuDestructiveItem,
    InfoItem: MenuInfoItem,
    CheckboxItem: MenuCheckboxItem,
    LinkItem: MenuLinkItem,
    RadioGroup: MenuRadioGroup,
    RadioItem: MenuRadioItem,
    Separator: MenuSeparator,
    SwitchItem: MenuSwitchItem,
    Submenu: MenuSubmenu,
    SubmenuItem: MenuSubmenuItem,
    SubmenuDrilldownItem: MenuSubmenuDrilldownItem,
    Group: MenuGroup,
    Header: MenuHeader,
    Footer: MenuFooter,
});
//# sourceMappingURL=index.js.map